import React from "react"
import { useSelector } from "react-redux"
import { Card } from "./ui/card"
import { Checkbox } from "./ui/checkbox"
import { cn } from "../lib/utils"
import BasketItemActions from "./BasketItemActions"
import { BasketItem, DropDownConfig } from "../types/basket"
import type { RootState } from "../store/types"
import useIsDesktop from "../hooks/useIsDesktop"

export interface BasketItemCardProps {
  imageUrl: string
  imageAlt?: string
  children: React.ReactNode
  className?: string
  upgradeLabel?: string
  showCheckbox?: boolean
  isChecked?: boolean
  onToggle?: (checked: boolean) => void
  imageContent?: React.ReactNode
  imageWrapperClassName?: string
  // Action controls props
  quantity?: number
  setCountValue?: (count: number) => void
  dropDowns?: DropDownConfig[]
  item?: BasketItem
  tuneSettingsFooter?: React.ReactNode
  tuneSettingsMobileFooter?: React.ReactNode
  upgradeBanner?: React.ReactNode
}

const BasketItemCard = ({
  imageUrl,
  imageAlt = "Product image",
  children,
  className,
  upgradeLabel,
  showCheckbox = false,
  isChecked = false,
  onToggle,
  imageContent,
  imageWrapperClassName,
  quantity,
  setCountValue,
  dropDowns,
  item,
  tuneSettingsFooter,
  tuneSettingsMobileFooter,
  upgradeBanner,
}: BasketItemCardProps) => {
  const dir = useSelector((state: RootState) => state.mode.direction)
  const isDesktop = useIsDesktop()

  return (
    <>
      <Card
        className={cn(
          "p-6 shadow-[0px_0px_8px_0px_rgba(0,0,0,0.25)]",
          " gap-4 md:gap-6 relative flex flex-col",
          className
        )}
        dir={dir}
        data-testid={`basket-item-card-${item?.item_id || 'unknown'}`}
      >
      {upgradeLabel && (
        <div
          className="bg-lupa-mint font-ploni rounded-lg pt-1 px-2 flex-0 w-fit"
        >
          {upgradeLabel}
        </div>
      )}
        <div className="flex flex-row gap-6 md:gap-8 flex-grow">
          <div className="flex-shrink-0 self-start relative">
            <div
              className={cn(
                "relative flex items-center justify-center overflow-hidden",
                "w-basket-img-sm h-basket-img-sm md:w-basket-img-lg md:h-basket-img-lg",
                imageWrapperClassName
              )}
            >
              {imageContent ? (
                imageContent
              ) : (
                <img
                  src={imageUrl}
                  alt={imageAlt}
                  className={cn(
                    "object-contain shadow-[3px_3px_9px_#DADADA] w-full h-full"
                  )}
                />
              )}
            </div>
          </div>
          {showCheckbox && (
            <div className="absolute top-[5px] start-[5px] z-1">
              <Checkbox
                onCheckedChange={onToggle}
                checked={isChecked}
                variant="neutral"
                className="h-5 w-5 md:h-7 md:w-7 rounded-full"
              />
            </div>
          )}
          <div className="flex-1 min-w-0 w-full flex flex-col">{children}</div>
        </div>


        {!isDesktop && upgradeBanner && (
          <div className="md:hidden mt-3">
            {upgradeBanner}
          </div>
        )}

        {!isDesktop && quantity !== undefined && setCountValue && (
          <div className="md:hidden mt-3">
            <div className="h-px w-full bg-disable mb-3" />
            <BasketItemActions
              quantity={quantity}
              setCountValue={setCountValue}
              dropDowns={dropDowns}
              imageUrl={imageUrl}
              item={item}
              tuneSettingsTitle={imageAlt}
              tuneSettingsFooter={tuneSettingsFooter}
              tuneSettingsMobileFooter={tuneSettingsMobileFooter}
            />
          </div>
        )}
      </Card>
    </>
  )
}

export default BasketItemCard
